import React from 'react';

import { observer } from 'mobx-react';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { Button } from 'components/Button';
import { CenteredCircularProgress } from 'components/Progress';
import { SubmissionVersionType } from 'utils/api/modules/entities/entities.types';

import { formatWindow, describeSource } from './formatters';
import {
  ListContainer,
  VersionButton,
  VersionHeading,
  VersionName,
  VersionMeta,
  ChevronSlot,
  InlineErrorContainer,
  InlineErrorText,
  LoadingContainer,
} from './styles';

// Every row is numbered, including the current one.
const nameFor = (entry: SubmissionVersionType): string => {
  if (entry.isCurrent) return `Version ${entry.version} (latest)`;
  if (entry.isFirst) return `Version ${entry.version} (first)`;
  return `Version ${entry.version}`;
};

const VersionList: React.FC<{
  loading: boolean;
  error: boolean;
  versions?: SubmissionVersionType[];
  displayed: SubmissionVersionType | null;
  onSelect: (entry: SubmissionVersionType) => void;
  onRetry: () => void;
}> = ({ loading, error, versions, displayed, onSelect, onRetry }): React.ReactElement => {
  if (loading) {
    return (
      <LoadingContainer data-testid="submission-version-history-loading">
        <CenteredCircularProgress aria-label="Loading version history" />
      </LoadingContainer>
    );
  }

  if (error || !versions) {
    return (
      <InlineErrorContainer data-testid="submission-version-history-error">
        <InlineErrorText>We could not load the version history.</InlineErrorText>
        <Button variant="text" onClick={onRetry} data-testid="submission-version-history-retry">
          Try again
        </Button>
      </InlineErrorContainer>
    );
  }

  return (
    <ListContainer data-testid="submission-version-list">
      {versions.map((entry) => {
        const isActive = entry.isCurrent ? displayed === null : displayed?.uuid === entry.uuid;

        return (
          <VersionButton
            key={entry.uuid}
            type="button"
            $isActive={isActive}
            aria-current={isActive}
            onClick={() => onSelect(entry)}
            data-testid={`submission-version-row-${entry.version}`}
          >
            <VersionHeading>
              <VersionName data-testid={`submission-version-row-name-${entry.version}`}>{nameFor(entry)}</VersionName>
              <ChevronSlot aria-hidden>
                <ChevronRightIcon />
              </ChevronSlot>
            </VersionHeading>
            <VersionMeta data-testid={`submission-version-row-window-${entry.version}`}>
              {formatWindow(entry)}
            </VersionMeta>
            {!(entry.isCurrent && entry.isFirst) && (
              <VersionMeta data-testid={`submission-version-row-source-${entry.version}`}>
                {describeSource(entry)}
              </VersionMeta>
            )}
          </VersionButton>
        );
      })}
    </ListContainer>
  );
};

export default observer(VersionList);
